import React, { useState, useMemo } from "react";
import classNames from "classnames/bind";
import Fuse from "fuse.js";

import { Checkbox } from "../Checkbox";
import { Button } from "../Button";

import { ReactComponent as Search } from "../../icons/search_small.svg";
import { ReactComponent as ChevronLeft } from "../../icons/chevron-left.svg";
import { ReactComponent as Cross } from "../../icons/cross_small.svg";

import styles from "./SearchBox.module.css";

const cx = classNames.bind(styles);

type Option = {
  value: string;
  label: string;
};

type Props = {
  title: string;
  placeholder: string;
  isLoading: boolean;
  options: Option[];
  onChange: (search: string, values: string[]) => void;
  onRemoveItem: (key: string) => void;
  minSearchLength?: number;
  value: string[];
};

const fuzzyFilterOptions = (
  options: Option[],
  search: string,
  exclude: string[]
) => {
  const withoutAdded = options.filter((item) => !exclude.includes(item.value));

  if (!search.length) {
    return withoutAdded;
  }

  const fuse = new Fuse(withoutAdded, {
    location: 0,
    distance: 100,
    threshold: 0.2,
    maxPatternLength: 100,
    keys: ["label"],
  });

  if (search.length === 1) {
    return withoutAdded.filter((item) =>
      item.label.toLowerCase().includes(search.toLowerCase())
    );
  }

  return fuse.search<Option>(search);
};

export const SearchBox: React.FC<Props> = ({
  title,
  placeholder,
  isLoading,
  options,
  onChange,
  onRemoveItem,
  minSearchLength = 0,
  value,
}) => {
  const [searchValue, setSearchValue] = useState<string>("");
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [selected, setSelected] = useState<string[]>([]);

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    setSelected([]);
  };

  const handleClose = () => {
    setIsFocused(false);
    handleSearchChange("");
  };

  const handleAdd = () => {
    onChange(searchValue, selected);
    handleClose();
  };

  const visibleOptions = useMemo(() => {
    if (searchValue.length >= minSearchLength) {
      return fuzzyFilterOptions(options, searchValue, value);
    }

    return [];
  }, [options, searchValue, minSearchLength, value]);

  const renderOptions = () => {
    if ((searchValue?.length || 0) < minSearchLength) {
      return null;
    }

    if (isLoading) {
      return "Loading...";
    }

    if (visibleOptions.length) {
      return (
        <ul className={cx("options")}>
          {visibleOptions.map((option) => (
            <li className={cx("option")} key={option.value}>
              <Checkbox
                checked={selected.includes(option.value)}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  if (event.target.checked) {
                    setSelected(selected.concat(option.value));
                  } else {
                    setSelected(
                      selected.filter((item) => item !== option.value)
                    );
                  }
                }}
              >
                {option.label}
              </Checkbox>
            </li>
          ))}
        </ul>
      );
    }

    if (visibleOptions.length === 0) {
      return (
        <ul className={cx("options")}>
          <li className={cx("option")}>Ничего не найдено</li>
        </ul>
      );
    }
  };

  return (
    <div
      className={cx("container", {
        container_focused: isFocused,
      })}
    >
      {!isFocused &&
        (value ? (
          <ul className={cx("options")}>
            {value.map((key) => {
              const option = options.find((item) => item.value === key);

              return (
                <li className={cx("option")} key={key}>
                  {isLoading ? "" : option?.label || key}
                  <button
                    className={cx("remove")}
                    type="button"
                    onClick={() => onRemoveItem(key)}
                  >
                    <Cross />
                  </button>
                </li>
              );
            })}
          </ul>
        ) : (
          <h2 className={cx("title")}>{title}</h2>
        ))}
      <div className={cx("inputContainer")}>
        {isFocused && (
          <button
            type="button"
            onClick={handleClose}
            className={cx("backButton")}
          >
            <ChevronLeft width={16} height={16} />
          </button>
        )}
        <input
          className={cx("input")}
          type="text"
          placeholder={placeholder}
          value={searchValue}
          onChange={(event) => handleSearchChange(event.target.value)}
          onFocus={() => setIsFocused(true)}
        />
        <Search width={16} height={16} className={cx("icon")} />
      </div>
      {isFocused && <div className={cx("content")}>{renderOptions()}</div>}
      {isFocused && (
        <footer className={cx("footer")}>
          <Checkbox
            checked={
              selected.length === visibleOptions.length &&
              !!visibleOptions.length
            }
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              if (event.target.checked) {
                setSelected(visibleOptions.map((option) => option.value));
              } else {
                setSelected([]);
              }
            }}
          >
            Выбрать все
          </Checkbox>
          <div className={cx("footerButton")}>
            <Button
              type="button"
              size="small"
              block
              onClick={handleAdd}
              disabled={!selected.length}
            >
              Добавить
            </Button>
          </div>
        </footer>
      )}
    </div>
  );
};
